import BaseUrl from '@/api/BaseUrl';
import Headers from '@/api/Headers';
import ENDPOINTS from '@/api/endpoints';
import { useSnackbarStore } from '@/stores/snackbarInfo';
import { useCheckoutStore } from '@/stores/checkoutStore';
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import isEmpty from 'lodash/isEmpty';

export function useEventRegistrationApi(route, is_paid_event = false) {
  const router = useRouter();
  const snackbar = useSnackbarStore();
  const checkoutStore = useCheckoutStore();

  const {
    data: allTicketSelectionData,
    error: errorLoadingTicketData,
    pending: loadingTicketData,
    refresh: refreshTicketSelection,
  } = useFetch(
    () =>
      `${BaseUrl.get(ENDPOINTS.EVENTS.GET_ALL_SELECTIONS)}${ENDPOINTS.EVENTS.GET_ALL_SELECTIONS}?event_id=${
        route.params.id
      }`,
    { headers: Headers, key: `allSelections_${route.params.id}` },
  );

  const ticketIds = computed(() => {
    const selections = allTicketSelectionData.value ?? [];
    if (selections.length === 0) return '';
    const idsString = selections.map((t) => `&id=${t.ticket_id}`).join('');
    const tableId = selections[0]?.table_id;
    return idsString + (tableId ? `&table_id=${tableId}` : '');
  });

  // Fetch ticket config per-selection (separate calls for ticket vs table) and merge
  const ticketData = ref([]);

  const refreshTicketConfig = async () => {
    const selections = allTicketSelectionData.value ?? [];
    if (!selections.length) {
      ticketData.value = [];
      return;
    }

    // Build per-selection URLs: include table_id when present
    const urls = selections.map((s) => {
      const base = `${BaseUrl.get(ENDPOINTS.EVENTS.GET_ORDER_TICKET_CONFIG)}${
        ENDPOINTS.EVENTS.GET_ORDER_TICKET_CONFIG
      }?event_id=${route.params.id}&id=${s.ticket_id}`;
      return s && s.table_id != null ? `${base}&table_id=${s.table_id}` : base;
    });

    try {
      const results = await Promise.all(
        urls.map((u) =>
          // GET request for each selection; returns data (object or array)
          $fetch(u, { headers: Headers }),
        ),
      );

      // Normalize responses to an array of ticket configs and merge by ticket_id
      const merged = [];
      results.forEach((r) => {
        if (Array.isArray(r)) merged.push(...r);
        else if (r) merged.push(r);
      });

      // Deduplicate by ticket_id; later entries (table-specific) will overwrite earlier ones
      const map = {};
      merged.forEach((item) => {
        if (item && item.ticket_id != null && item.table_id != null) map[item.table_id] = item;
        else if (item && item.ticket_id != null) map[item.ticket_id] = item;
      });
      ticketData.value = Object.values(map);
    } catch (e) {
      console.error('Error fetching ticket config per selection:', e);
      ticketData.value = [];
    }
  };

  const ticketMap = computed(() =>
    ticketData.value?.reduce((acc, curr) => {
      if (curr.ticket_id != null && curr.table_id != null) acc[curr.table_id] = curr;
      else if (curr.ticket_id != null) acc[curr.ticket_id] = curr;
      return acc;
    }, {}),
  );

  // Fetch ticket config whenever selections change
  watch(
    allTicketSelectionData,
    () => {
      refreshTicketConfig();
    },
    { immediate: true },
  );

  const addonSummary = computed(() => {
    const summary = {};
    ticketData.value?.forEach((ticket) => {
      ticket.all_available_addons?.forEach((addon) => {
        summary[addon.id] = addon.available_qty;
      });
    });
    return summary;
  });

  const errorPostingOrderDetails = ref(null);
  const orderDetailsResponse = ref({});
  const resgistrationResponse = ref({});
  const billMeLater = ref(false);
  const pendingTotalAmount = ref(null);

  const postOrderDetails = async (body) => {
    billMeLater.value = !!(body && body.bill_me_later);
    pendingTotalAmount.value = body?.total_amount ?? null;

    const orderDetailsUrl = `${BaseUrl.get(ENDPOINTS.EVENTS.POST_ORDER_DETAILS)}${
      ENDPOINTS.EVENTS.POST_ORDER_DETAILS
    }?event_id=${route.params.id}`;

    try {
      errorPostingOrderDetails.value = null;
      const data = await $fetch.raw(orderDetailsUrl, {
        headers: Headers,
        method: 'POST',
        body,
      });

      orderDetailsResponse.value = data;
    } catch (e) {
      errorPostingOrderDetails.value = e;
    }
  };

  const refreshAllData = async () => {
    await refreshTicketSelection();
    await refreshTicketConfig();
  };

  watch(errorPostingOrderDetails, (error) => {
    if (!error) return;
    if (error.status === 410) {
      //TODO: go to waitlist page
    }
    snackbar.setSnackbar('Could not process order at this time', 'error', 2000);
  });

  watch(orderDetailsResponse, async (response) => {
    if (isEmpty(response)) return;

    const { cartId, message } = response._data;
    if (message) {
      const errorMessages = message.map((msg) => `• ${msg}`).join('\n');
      snackbar.setSnackbar(errorMessages, 'error', 8000);
      await refreshAllData();
      return;
    }

    const isZeroCart = pendingTotalAmount.value !== null && pendingTotalAmount.value === 0;
    if (cartId && (!is_paid_event || !!billMeLater.value || isZeroCart)) {
      checkoutStore.setSelections(null, null);
      const submitRegistrationUrl = `${BaseUrl.get(ENDPOINTS.EVENTS.SUBMIT_REGISTRATION_V2)}${
        ENDPOINTS.EVENTS.SUBMIT_REGISTRATION_V2
      }?event_id=${route.params.id}`;

      const cartData = {
        cart_id: cartId,
        bill_me_later: !!billMeLater.value,
      };

      try {
        resgistrationResponse.value = await $fetch.raw(submitRegistrationUrl, {
          headers: Headers,
          method: 'POST',
          body: cartData,
          ignoreResponseError: true,
        });

        checkoutStore.setRegistrationResponse(resgistrationResponse.value?._data);

        if (resgistrationResponse.value?.status !== 201) {
          const errorResp =
            resgistrationResponse.value?._data?.message || 'Error submitting registration';
          snackbar.setSnackbar(errorResp, 'error', 4000);
          return;
        }
        router.push({
          name: `attendance-id-confirmation-cartId`,
          params: {
            id: route.params.id,
            cartId: cartId,
          },
        });
      } catch (e) {
        snackbar.setSnackbar(`Error Submitting Registration`, 'error', 4000);
      }
    } else if (cartId) {
      checkoutStore.setSelections(null, null);

      router.push({
        name: 'checkout-eventId-cartId',
        params: {
          eventId: route.params.id,
          cartId: cartId,
        },
        query: {
          product: 'attendance',
        },
      });
    }
  });

  return {
    allTicketSelectionData,
    errorLoadingTicketData,
    loadingTicketData,
    ticketMap,
    addonSummary,
    postOrderDetails,
    refreshAllData,
  };
}
